import React, { useEffect, useState, useMemo, useContext } from 'react';
import proptypes from 'prop-types';
import { InfoFilled } from '@one-thd/sui-icons';
import {
  useDataModel,
  params,
  extend,
  arrayOf as arrayType,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { StoreLayout } from '@thd-olt-component-react/store-layout';
import { StoreLayoutModal } from './StoreLayoutModal';
import { dataModel } from './dataModel';
import { ready, returnObject } from '../helper';

const { arrayOf, shape, string } = proptypes;

export const AisleInfo = ({ itemId, products, searchKeyword }) => {
  const store = useStore({ varnish: true, online: true });
  const { storeId, storeName } = store;
  const storeSkuIds = (products || []).map((product) => product.identifiers?.storeSkuNumber)
    .filter((sku) => !!sku);
  const { channel } = useContext(ExperienceContext);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const isDesktop = channel === 'desktop';

  const aisleBayResponse = useDataModel('aislebay', {
    skip: !storeSkuIds.length || !storeId || !storeName,
    ssr: false,
    variables: {
      storeId,
      storeSkuIds
    }
  });

  useEffect(() => {
    ready('aisle-info');
  }, []);

  const { aislebay } = returnObject(aisleBayResponse.data);
  const { storeSkus = [] } = returnObject(aislebay);

  const mostCommonAisle = useMemo(() => {
    let tempMostCommonAisle = { count: 0, aisle: '' };
    const counts = {};
    storeSkus.forEach((sku) => {
      const { aisleBayInfo } = sku;
      const { aisle, bay } = returnObject(aisleBayInfo);
      if (!(aisle && bay)) {
        return;
      }
      counts[aisle] = counts[aisle] ? counts[aisle] + 1 : 1;
      tempMostCommonAisle = counts[aisle] > tempMostCommonAisle.count
        ? { ...aisleBayInfo, count: counts[aisle] }
        : tempMostCommonAisle;
    });

    return tempMostCommonAisle;
  }, [storeSkus]);

  if (!itemId?.length && !products?.length) {
    return null;
  }

  if (!storeSkuIds.length || (aislebay && !storeSkus.length)) {
    return null;
  }

  const shouldBeHidden = !storeName || !mostCommonAisle.aisle;
  const commonAisle = mostCommonAisle.aisle;
  const friendlyAisleDescription = storeSkus?.[0]?.aisleBayInfo?.invLocDescFriendly;

  const onStoreLayoutClick = () => {
    if (isDesktop) {
      return;
    } else {
      window.LIFE_CYCLE_EVENT_BUS.trigger('NATIVE_APP.STORE_MAP_PLP', {
        storeId,
        storeName,
        commonAisle,
        searchKeyword,
        friendlyAisleDescription
      });
      setStoreModalOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`
          sui-flex sui-flex-row sui-w-full sui-bg-info-subtle sui-pt-1 sui-pb-1 sui-pr-2 sui-pl-2
          sui-text-left sui-items-center
          ${!isDesktop ? 'sui-cursor-pointer' : 'sui-cursor-text'}
          ${shouldBeHidden ? 'invisible' : 'visible'}`}
        onClick={onStoreLayoutClick}
        data-component="AisleInfo"
      >
        <InfoFilled size="small" color="info" />
        <div className="sui-flex sui-flex-col sui-text-base sui-ml-2">
          <span>Most of these items are found on&nbsp;
            <span className="sui-font-bold">
              {`Aisle ${mostCommonAisle.aisle}`}
            </span>
          </span>
        </div>
      </button>
      <StoreLayoutModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
        storeId={storeId}
        storeName={storeName}
        aisle={mostCommonAisle.aisle}
        bay={mostCommonAisle.bay}
      />
    </>
  );

};

AisleInfo.displayName = 'AisleInfo';

AisleInfo.propTypes = {
  itemId: string,
  searchKeyword: string,
  products: arrayOf(shape({})).isRequired
};

AisleInfo.dataModel = extend({}, dataModel, {
  aislebay: params({
    storeId: stringType().isRequired(),
    storeSkuIds: arrayType(stringType().isRequired())
  }).shape({
    storeSkus: arrayType(shapeType({
      storeNumber: stringType(),
      storeSkuId: stringType(),
      aisleBayInfo: shapeType({
        aisle: stringType(),
        bay: stringType(),
        invLocDesc: stringType()
      })
    }))
  })
}, StoreLayout);

AisleInfo.defaultProps = {
  itemId: null,
  searchKeyword: null
};
